import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { MeetingsTabKey } from "../MeetingsTabsView.types";

type Props = {
  activeTab: MeetingsTabKey;
  onChangeTab: (tab: MeetingsTabKey) => void;
  onPressFilter?: () => void;
  onPressCreateMeeting?: () => void;
  onPressConversationStarters?: () => void;

  renderAllMeetings: React.ReactNode;
  renderMyMeetings: React.ReactNode;
};

export default function MeetingsTabsView({
  activeTab,
  onChangeTab,
  onPressFilter,
  onPressCreateMeeting,
  onPressConversationStarters,
  renderAllMeetings,
  renderMyMeetings,
}: Props) {
  return (
    <View style={styles.container}>
      <View style={styles.tabContainer}>
        <View style={styles.tabsWrapper}>
          <TouchableOpacity
            style={[
              styles.tab,
              styles.leftTab,
              activeTab === "AllMeetings" && styles.activeTab,
            ]}
            onPress={() => onChangeTab("AllMeetings")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "AllMeetings" && styles.activeTabText,
              ]}
            >
              Available Conversations
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.tab,
              styles.rightTab,
              activeTab === "MyMeetings" && styles.activeTab,
            ]}
            onPress={() => onChangeTab("MyMeetings")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "MyMeetings" && styles.activeTabText,
              ]}
            >
              My Conversations
            </Text>
          </TouchableOpacity>
        </View>

        <TouchableOpacity style={styles.filterButton} onPress={onPressFilter}>
          <Text style={styles.filterText}>FILTER</Text>
        </TouchableOpacity>
      </View>

      {onPressConversationStarters && (
        <TouchableOpacity
          style={styles.startersRow}
          onPress={onPressConversationStarters}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Browse conversation starters"
        >
          <Ionicons name="chatbubbles-outline" size={18} color="#3A6FB1" />
          <Text style={styles.startersText}>
            Not sure what to say? Browse conversation starters
          </Text>
          <Ionicons name="chevron-forward" size={16} color="#3A6FB1" />
        </TouchableOpacity>
      )}

      <View style={styles.content}>
        {activeTab === "AllMeetings" ? renderAllMeetings : renderMyMeetings}
      </View>

      {onPressCreateMeeting && (
        <TouchableOpacity
          style={styles.floatingCreateButton}
          onPress={onPressCreateMeeting}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel="Create conversation"
        >
          <Ionicons name="add" size={24} color="#FFFFFF" />
          <Text style={styles.floatingCreateText}>CREATE</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F7F9",
  },
  tabContainer: {
    flexDirection: "row",
    paddingHorizontal: 40,
    paddingTop: 16,
    paddingBottom: 16,
    backgroundColor: "#F6F7F9",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tabsWrapper: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  tab: {
    paddingVertical: 24,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: "#D8DBE1",
    backgroundColor: "#FFFFFF",
  },
  leftTab: {
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
    borderRightWidth: 0,
  },
  rightTab: {
    borderTopRightRadius: 16,
    borderBottomRightRadius: 16,
  },
  activeTab: {
    backgroundColor: "#3A6FB1",
  },
  tabText: {
    fontSize: 14,
    fontWeight: "700",
    fontFamily: "Lexend",
    textTransform: "uppercase",
    color: "#2B2B2F",
  },
  activeTabText: {
    color: "#F6F7F9",
  },
  floatingCreateButton: {
    position: "absolute",
    bottom: 80,
    right: 40,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#3A6FB1",
    paddingVertical: 14,
    paddingHorizontal: 22,
    borderRadius: 100,
    gap: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
  },
  floatingCreateText: {
    fontSize: 14,
    fontWeight: "700",
    fontFamily: "Lexend",
    color: "#FFFFFF",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  startersRow: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    marginHorizontal: 40,
    marginBottom: 12,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: "#EAF0F8",
    gap: 8,
  },
  startersText: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Lexend",
    color: "#3A6FB1",
  },
  content: {
    flex: 1,
  },
  filterButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  filterText: {
    fontSize: 14,
    fontWeight: "700",
    fontFamily: "Lexend",
    textTransform: "uppercase",
    color: "#3A6FB1",
  },
});
